/* Copyright (c) 2020-present CloudIO Inc */

import {
  Theme,
  InputBase,
  IconButton,
  Divider,
  Typography,
  Menu,
  MenuItem,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  getIoDatasourceStoreProps,
  recordId,
  useIoDatasourceAttributesStore,
} from 'store/utils/utils';
import {
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoDatasource,
  IoDatasourceAttributes,
  isDateType,
  isStringType,
} from '@cloudio-saas/datasource-types';
import useDataStore from 'store/useDataStore';
import {
  useStoreCurrentRecord,
  useStoreBusy,
  useStoreDirty,
} from 'store/useAppStore';
import { useDeferredValue } from 'experimental/ExperimentalReact';
import { isFromDB } from 'store/storeTypes';
import { PlatformContext } from 'platform/PlatformProvider';
import Icon from 'icons/Icon';
import WaveDots from 'utils/WaveDots';
import { isEmpty } from 'utils/isEmpty';
import ExpandCollapseIcon from 'layout/Sidebar/panels/ExpandCollapseIcon';
import {
  DSView,
  sidebarActiveAttributeIdAtom,
  sidebarActiveDatasourceIdAtom,
  sidebarAttributesFilterAtom,
  sidebarAttributesMissingWhoAtom,
  sidebarAttributesShowRequiredAtom,
  sidebarSelectedAttributesListAtom,
  WHO,
  WHOAttributes,
} from '../../atoms';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { isViewOnlyDeveloper } from 'root/useUser';
import { usePending } from 'platform/PlatformHooks';
import { TypographyEllipsisProps } from 'utils/commonProps';

const whoNameMap: Record<WHO, string> = {
  createdBy: 'Created By',
  lastUpdatedBy: 'Last Updated By',
  creationDate: 'Creation Date',
  lastUpdateDate: 'Last Update Date',
};

const whoColumnMap: Record<WHO, string> = {
  createdBy: 'CREATED_BY',
  lastUpdatedBy: 'LAST_UPDATED_BY',
  creationDate: 'CREATION_DATE',
  lastUpdateDate: 'LAST_UPDATE_DATE',
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

const getIconName = (view: string): string => {
  if (view === 'editChild') {
    return 'check';
  }
  return 'plus';
};

export default function AttributesHeader({
  setView,
  view,
  appUid,
}: {
  appUid: string;
  setView: (v: DSView) => void;
  view: DSView;
}): React.ReactElement {
  const ds = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  const store = useIoDatasourceAttributesStore(appUid);
  const platform = useContext(PlatformContext);
  const setDsAttrId = useStateXAtomSetter(sidebarActiveAttributeIdAtom);
  const attrStore = useDataStore<IoDatasourceAttributes>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceAttributes',
    alias: `IoDatasourceAttributes-chooser-${ds}`,
  });
  const dirty = useStoreDirty(store);
  const busy = useStoreBusy(store);

  const records = useStoreRecords(store);
  const [missingWhoAttrs, setMissingWhoAttrs] = useStateXAtom(
    sidebarAttributesMissingWhoAtom,
  );

  const [isPending, setPending] = usePending();
  const [showPopup, setShowPopup] = useState<boolean>(false);

  useEffect(() => {
    const whoColumns = records.filter((a) =>
      WHOAttributes.includes((a.attributeCode ?? '') as WHO),
    );
    if (whoColumns.length !== 4) {
      const _missingWhoAttrs = WHOAttributes.filter(
        (attr) => !whoColumns.find((a) => a.attributeCode === attr),
      );
      setMissingWhoAttrs(_missingWhoAttrs);
    } else {
      setMissingWhoAttrs(sidebarAttributesMissingWhoAtom.defaultValue);
    }
  }, [records]);

  const { record } = useStoreCurrentRecord(store);
  const [selectedAttibutes, setSelectedAttibutes] = useStateXAtom(
    sidebarSelectedAttributesListAtom,
    { params: { ds: record?.datasourceUid ?? '' } },
  );
  const classes = useStyles();

  const dsStore = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));

  const { record: curDSRecord } = useStoreCurrentRecord(dsStore);

  const iconName = useMemo(() => getIconName(view), [view]);

  const [search, setSearch] = useStateXAtom(sidebarAttributesFilterAtom);
  const [showReqFields, setShowReqFields] = useStateXAtom(
    sidebarAttributesShowRequiredAtom,
  );

  const [filter, setFilter] = useState(search);
  const deferredFilter = useDeferredValue(filter);
  useEffect(() => {
    setSearch(deferredFilter);
  }, [deferredFilter, setSearch]);

  const ref = useRef<HTMLInputElement>(null);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFilter(e.target.value);
  }

  const [anchorElFilter, setAnchorElFilter] = useState<HTMLElement | undefined>(
    undefined,
  );
  const openFilter = Boolean(anchorElFilter);
  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorElFilter(event.currentTarget);
  };

  const handleOnMenuClose = () => {
    setAnchorElFilter(undefined);
  };

  const [anchorWhoEl, setAnchorWhoEl] = useState<HTMLElement | undefined>(
    undefined,
  );
  const handleOnWhoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorWhoEl(event.currentTarget);
  };

  const handleOnWhoMenuClose = () => {
    setAnchorWhoEl(undefined);
  };

  const handleConfirmationPopup = async (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setShowPopup(true);
  };

  const handleRefresh = () => {
    platform.startAsyncTransition(async () => {
      setSelectedAttibutes([]);
      await store.refresh(true);
    }, setPending);
  };

  const handleDelete = () => {
    platform.startAsyncTransition(async () => {
      selectedAttibutes.forEach((id) => {
        store.setRecordStatus(id, 'D');
      });
      store
        .save({
          feedback: ` ${selectedAttibutes.length} ${
            selectedAttibutes.length > 1 ? 'Attributes' : 'Attribute'
          } deleted successfully!`,
        })
        .then((res) => {
          if (res) {
            handleRefresh();
          }
        });
    }, setPending);
    setShowPopup(false);
  };

  const close = () => {
    setShowPopup(false);
  };

  const [savingKey, setSavingKey] = useState('');
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  const menuItems: React.ReactElement[] = [];
  if (anchorWhoEl) {
    missingWhoAttrs.forEach((attr) => {
      records
        .sort((a, b) =>
          a.attributeCode && b.attributeCode
            ? a.attributeCode.localeCompare(b.attributeCode)
            : 0,
        )
        .filter(
          (r) =>
            r.attributeType &&
            (['creationDate', 'lastUpdateDate'].includes(attr)
              ? isDateType(r.attributeType)
              : isStringType(r.attributeType)) &&
            r.attributeCode &&
            ![
              'createdBy',
              'creationDate',
              'lastUpdateDate',
              'lastUpdatedBy',
            ].includes(r.attributeCode),
        )
        .forEach((r) => {
          menuItems.push(
            <MenuItem
              key={`${attr}-${r.attributeCode}`}
              disabled={`${attr}-${r.attributeCode}` === savingKey}
              onClick={async (e) => {
                if (savingKey) return;
                e.preventDefault();
                e.stopPropagation();
                setSavingKey(`${attr}-${r.attributeCode}`);
                store.updateRecord(recordId(r), { attributeCode: attr });
                await store.save({
                  feedback: `Field ${r.attributeName} is set as ${whoNameMap[attr]}`,
                });
                setSavingKey('');
              }}
            >
              {`${attr}-${r.attributeCode}` === savingKey ? (
                <WaveDots active />
              ) : null}
              <Typography>
                Use [{r.attributeName}] as {whoNameMap[attr]}
              </Typography>
            </MenuItem>,
          );
        });

      menuItems.push(
        <MenuItem
          key={attr}
          onClick={async (e) => {
            if (savingKey) return;
            e.preventDefault();
            e.stopPropagation();
            if (curDSRecord) {
              handleOnWhoMenuClose();
              if (dirty && !record?.attributeCode) {
                store?.refresh();
              }
              await store.createNew({
                partialRecord: {
                  attributeType: ['creationDate', 'lastUpdateDate'].includes(
                    attr,
                  )
                    ? 'DateTime'
                    : 'String',
                  attributeCode: attr,
                  attributeName: whoNameMap[attr],
                  columnName: whoColumnMap[attr],
                  maxLength: ['creationDate', 'lastUpdateDate'].includes(attr)
                    ? undefined
                    : 128,
                  datasourceUid: curDSRecord.datasourceUid,
                  appUid,
                  seqNo: (store.recordsOrder().length + 1) * 10,
                  allowBlank: 'N',
                  updateAllowed: ['creationDate', 'createdBy'].includes(attr)
                    ? 'N'
                    : 'Y',
                },
                rs: 'I',
              });
              // clear the active attr id to avoid querying in the edit attribute UI
              setDsAttrId('');
              setView('editChild');
            }
          }}
        >
          <Typography>Add new field {whoNameMap[attr]}</Typography>
        </MenuItem>,
      );
    });
  }

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back"
        onClick={() => {
          if (view === 'editChild') {
            store.reset();
            setView('listChildren');
          } else {
            setView('list');
          }
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Back
        </span>
        <Icon iconName="chevron-left" />
      </IconButton>
      {view === 'editChild' ? (
        <Typography
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          className={classes.input}
        >
          {isFromDB(record) ? 'Edit' : 'Add New'} Field [
          {curDSRecord?.datasourceUid}]
        </Typography>
      ) : (
        <InputBase
          data-testid="search-fields"
          inputRef={ref}
          className={classes.input}
          placeholder={`Search Fields [${curDSRecord?.datasourceUid}]`}
          inputProps={{ 'aria-label': 'search fields' }}
          value={filter ?? ''}
          onChange={onChange}
          autoFocus
        />
      )}
      <Divider className={classes.divider} orientation="vertical" />
      {view !== 'editChild' || dirty ? (
        <IconButton
          data-testid="action"
          disabled={busy || isViewOnlyUser}
          color="primary"
          aria-label={view === 'editChild' ? 'Save' : 'Add Attribute'}
          data-tip={view === 'editChild' ? 'Save' : 'Add Attribute'}
          onClick={async (e) => {
            if (view === 'editChild') {
              const row = store.getCurrentRecord();
              if (!row) {
                platform.showError('Missing current record!');
                return;
              }
              if (isEmpty(row.columnName)) {
                platform.showError('Missing Column Name!');
                return;
              }
              if (isEmpty(row.attributeName)) {
                platform.showError('Missing Field Label!');
                return;
              }
              if (isEmpty(row.attributeCode)) {
                platform.showError('Missing Field Code!');
                return;
              }
              if (row.datasourceUid !== curDSRecord?.datasourceUid) {
                platform.showError(
                  `Current dataSource [${curDSRecord?.datasourceUid}] doesn't match with the dataSource [${row.datasourceUid}] on the field!`,
                );
                return;
              }
              if (row.attributeType === 'Reference') {
                const refMd = row.refMd;
                if (!refMd?.datasourceUid) {
                  platform.showError('Missing reference details!');
                  return;
                }
                if (!refMd.attributeCode) {
                  platform.showError('Missing reference attribute!');
                  return;
                }
                if (!refMd.attributeType) {
                  platform.showError('Missing reference attribute type!');
                  return;
                }
                if (!refMd.selectAttributes.length) {
                  platform.showError(
                    'Missing select attribute from the reference data source!',
                  );
                  return;
                }
              }
              const fromDB = isFromDB(row);
              const r = await store.save({ feedback: 'NONE' });
              if (r && curDSRecord) {
                if (attrStore) {
                  await attrStore.query({
                    props: {
                      filter: [
                        { datasourceUid: { is: curDSRecord.datasourceUid } },
                        { appUid: { is: appUid } },
                      ],
                      limit: 5000,
                      force: true,
                    },
                  });
                }
                let feedback = '';
                if (fromDB) {
                  setView('listChildren');
                  feedback = `Field ${row?.attributeName} updated successfully!`;
                } else if (row && curDSRecord) {
                  const {
                    // remove all _id & who columns... DO NOT REMOVE THESE AS UNUSED VARIABLES
                    _id,
                    _cid,
                    attributeCode,
                    attributeName,
                    columnName,
                    createdBy,
                    creationDate,
                    lastUpdateDate,
                    lastUpdatedBy,
                    _orig,
                    _rs,
                    // remove all _id & who columns... DO NOT REMOVE THESE AS UNUSED VARIABLES
                    ...others
                  } = row;
                  await store.createNew({
                    partialRecord: {
                      ...others,
                      datasourceUid: curDSRecord.datasourceUid,
                      appUid,
                      seqNo: (store.recordsOrder().length + 1) * 10,
                    },
                    rs: 'I',
                  });
                  feedback = `Field ${row?.attributeName} created successfully!`;
                }
                platform.showSuccess(feedback);
              }
            } else if (curDSRecord) {
              await store.createNew({
                partialRecord: {
                  attributeType: 'String',
                  maxLength: 40,
                  datasourceUid: curDSRecord.datasourceUid,
                  appUid,
                  seqNo: (store.recordsOrder().length + 1) * 10,
                },
                rs: 'I',
              });
              // clear the active attr id to avoid querying in the edit attribute UI
              setDsAttrId('');
              setView('editChild');
            }
          }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            {view === 'editChild' ? 'Save' : 'Add Attribute'}
          </span>
          {busy ? (
            <WaveDots active />
          ) : (
            <Icon
              disabled={isViewOnlyUser}
              color="primary"
              iconName={iconName}
            />
          )}
        </IconButton>
      ) : null}
      {view !== 'editChild' ? (
        <IconButton
          disabled={selectedAttibutes.length > 0 ? false : true}
          data-tip="Delete Selected"
          color="primary"
          aria-label="delete-button"
          className={classes.iconButton}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleConfirmationPopup(e);
          }}
        >
          {isPending ? (
            <WaveDots active style={{ marginLeft: 8 }} />
          ) : (
            <Icon
              disabled={selectedAttibutes.length > 0 ? false : true}
              color="error"
              iconName="trash-alt"
            />
          )}
        </IconButton>
      ) : (
        ''
      )}
      {showPopup ? (
        <Dialog open={showPopup}>
          <DialogTitle
            sx={{ p: 0, borderBottom: '2px solid #157CEC', mb: '12px' }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  pl: '8px',
                }}
              >
                <Icon iconName="info-circle" iconType="regular" />
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  sx={{ ...TypographyEllipsisProps, p: '12px', pl: 0 }}
                >
                  {'Confirm Deletion'}
                </Typography>
              </Box>
              <IconButton
                sx={{ ...TypographyEllipsisProps, p: '12px', pl: 0 }}
                onClick={() => close()}
              >
                <span aria-hidden="false" style={{ display: 'none' }}>
                  Close
                </span>
                <Icon iconName="times" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography fontSize="17px" gap="8px" pb="8px">
                Are you sure you want to delete these attributes?
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '4px',
                  pl: '16px',
                  flexDirection: 'column',
                }}
              >
                {selectedAttibutes.map((value) => {
                  const words = value.split('-');
                  const lastWord = words.pop();
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '16px',
                      }}
                    >
                      <Icon
                        iconName={'times'}
                        color={'error'}
                        height={12}
                        width={12}
                        iconType="regular"
                      />
                      <b>{`${
                        records.find(
                          (r) =>
                            lastWord &&
                            r.attributeCode &&
                            r.attributeUid &&
                            (lastWord.match(r.attributeCode) ||
                              lastWord.match(r.attributeUid)),
                        )?.attributeName
                      }`}</b>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <DialogActions sx={{ p: '8px 20px 16px 8px' }}>
              <Button
                data-testid="confirm-cancel"
                startIcon={
                  <Icon
                    iconName="times"
                    height={12}
                    width={12}
                    color={'#fff'}
                  />
                }
                onClick={() => close()}
                variant="contained"
                color="info"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-yes"
                onClick={() => handleDelete()}
                startIcon={
                  <Icon
                    iconName="check"
                    height={12}
                    width={12}
                    color={'#fff'}
                  />
                }
                variant="contained"
                color="success"
              >
                Yes
              </Button>
            </DialogActions>
          </DialogActions>
        </Dialog>
      ) : null}
      {missingWhoAttrs.length ? (
        <IconButton
          data-tip={`Missing WHO fields ${missingWhoAttrs.join(', ')}`}
          onClick={handleOnWhoClick}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Missing WHO fields
          </span>
          <Icon color="error" iconName="exclamation-triangle" />
        </IconButton>
      ) : null}
      {view !== 'editChild' ? (
        <IconButton data-testid="filters" onClick={handleOnClick}>
          <span aria-hidden="false" style={{ display: 'none' }}>
            Filter
          </span>
          <Icon
            color={showReqFields ? 'primary' : undefined}
            iconName="sliders-h"
          />
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
      {anchorElFilter && openFilter ? (
        <Menu
          anchorEl={anchorElFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          open={openFilter}
          onClose={handleOnMenuClose}
        >
          <MenuItem
            data-testid="viewMandatoryFields"
            key="viewMandatoryFields"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorElFilter(undefined);
              setShowReqFields(!showReqFields);
            }}
          >
            <Icon
              iconName="eye"
              sx={{ mr: '8px' }}
              color={showReqFields ? 'primary' : 'default'}
            />
            <Typography color={showReqFields ? 'primary' : 'default'}>
              View Mandatory Fields
            </Typography>
          </MenuItem>
        </Menu>
      ) : null}

      {anchorWhoEl ? (
        <Menu
          anchorEl={anchorWhoEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          open
          onClose={handleOnWhoMenuClose}
        >
          {menuItems}
        </Menu>
      ) : null}
    </div>
  );
}
